import { createLogger } from "../utils/logger.js";
//#region ../src/ar/image-capture.ts
/**
* Image Capture Module
*
* Captures periodic JPEG screenshots from the WebGL canvas during AR recording.
* Uses async toBlob() for better performance (non-blocking).
*
* Includes validation to detect suspiciously small images (likely black/empty)
* which can occur on mobile devices when the WebGL context hasn't composited yet.
*/
const log = createLogger("ImageCapture");
/**
* Minimum expected blob size in bytes for a valid JPEG image.
* A black/empty 1920x1080 JPEG still compresses to ~2-3KB due to headers.
* A real camera frame at 0.7 quality should be 50KB+.
* We use a conservative threshold to catch obviously broken captures.
*/
const MIN_VALID_IMAGE_BYTES = 5e3;
/**
* Default capture configuration
*/
const DEFAULT_CAPTURE_CONFIG = {
	intervalMs: 2e3,
	quality: .7,
	captureTimeoutMs: 5e3
};
/**
* Manages periodic image capture from a canvas.
*
* Usage:
* 1. Create with canvas and callbacks
* 2. Call start() when recording begins
* 3. Call onFrame(time) each XR frame
* 4. Call stop() when recording ends
*/
var ImageCaptureManager = class {
	canvas;
	callbacks;
	config;
	capturing = false;
	lastCaptureTime = 0;
	frameCount = 0;
	captureInProgress = false;
	captureTimeoutId = null;
	constructor(canvas, callbacks, config = DEFAULT_CAPTURE_CONFIG) {
		this.canvas = canvas;
		this.callbacks = callbacks;
		this.config = config;
	}
	/**
	* Start capturing images.
	* Resets frame counter and last capture time.
	*/
	start() {
		this.capturing = true;
		this.lastCaptureTime = 0;
		this.frameCount = 0;
	}
	/**
	* Stop capturing images.
	* Clears any pending safety timeout and resets in-flight capture state.
	*/
	stop() {
		this.capturing = false;
		this.clearCaptureTimeout();
		this.captureInProgress = false;
	}
	/**
	* Check if capture manager is active.
	*/
	isCapturing() {
		return this.capturing;
	}
	/**
	* Get the current frame count.
	*/
	getFrameCount() {
		return this.frameCount;
	}
	/**
	* Called each XR frame to check if a capture is needed.
	*
	* @param time - Frame timestamp in milliseconds (from requestAnimationFrame)
	*/
	onFrame(time) {
		if (!this.capturing) return;
		if (this.captureInProgress) return;
		const elapsed = time - this.lastCaptureTime;
		if (this.lastCaptureTime > 0 && elapsed < this.config.intervalMs) return;
		const pose = this.callbacks.getCurrentPose();
		if (!pose) return;
		this.captureInProgress = true;
		this.lastCaptureTime = time;
		const timestamp = Date.now();
		const screenRotation = this.callbacks.getScreenRotation();
		const frameIndex = ++this.frameCount;
		this.captureTimeoutId = setTimeout(() => {
			if (this.captureInProgress) {
				log.error(`Capture timeout after ${this.config.captureTimeoutMs}ms — force-resetting captureInProgress`);
				this.captureInProgress = false;
				this.captureTimeoutId = null;
			}
		}, this.config.captureTimeoutMs);
		if (this.callbacks.captureFrame) this.callbacks.captureFrame(this.config.quality).then((blob) => {
			this.handleCapturedBlob(blob, timestamp, frameIndex, pose, screenRotation);
		}).catch(() => {
			this.clearCaptureTimeout();
			this.captureInProgress = false;
			this.callbacks.onCaptureFailed?.();
		});
		else this.canvas.toBlob((blob) => {
			this.handleCapturedBlob(blob, timestamp, frameIndex, pose, screenRotation);
		}, "image/jpeg", this.config.quality);
	}
	/**
	* Clear the safety timeout for captureInProgress.
	* Called when capture completes (success or failure) before the timeout fires.
	*/
	clearCaptureTimeout() {
		if (this.captureTimeoutId !== null) {
			clearTimeout(this.captureTimeoutId);
			this.captureTimeoutId = null;
		}
	}
	/**
	* Common handler for captured blobs (from either canvas.toBlob or captureFrame).
	* Handles null blobs, suspicious image detection, and dispatching onCaptured.
	*/
	handleCapturedBlob(blob, timestamp, frameIndex, pose, screenRotation) {
		this.clearCaptureTimeout();
		this.captureInProgress = false;
		if (!blob) {
			this.callbacks.onCaptureFailed?.();
			return;
		}
		if (blob.size < 5e3) {
			log.error(`Suspicious image at frame ${frameIndex}: blob size ${blob.size} bytes is below minimum ${MIN_VALID_IMAGE_BYTES} bytes. Image may be black/empty.`);
			this.callbacks.onSuspiciousImage?.(blob.size, frameIndex);
		}
		this.callbacks.onCaptured({
			blob,
			timestamp,
			frameIndex,
			position: pose.position,
			rotation: pose.orientation,
			screenRotation
		});
	}
};
//#endregion
export { DEFAULT_CAPTURE_CONFIG, ImageCaptureManager, MIN_VALID_IMAGE_BYTES };
